import { profileStore } from 'mobx/ProfileStore';
import { useEffect } from 'react';
import {
  useFetchList,
  useFetchSingleData,
} from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

/**
 * Custom hook for fetching user profile data.
 * Centralizes the profile fetching logic to avoid duplication across components.
 * Automatically syncs fetched data to MobX ProfileStore.
 *
 * @param userId - The user ID to fetch profile for
 * @param options - Additional React Query options
 * @returns Profile data, loading state, and refetch function
 */
export const useUserProfile = (userId?: string, options: any = {}) => {
  const {
    data: profileResp,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useFetchSingleData<any>(
    ['profile', userId],
    userId || '',
    MASTER_ENDPOINT.Admins,
    { enabled: !!userId, ...options, refetchOnMount: 'always' },
  );

  const profileData = profileResp?.data?.[0];

  // Automatically sync profile data to MobX store
  useEffect(() => {
    if (profileData) {
      // Update country code if available
      if (profileData.countryCode) {
        profileStore.setCountryCode(profileData.countryCode);
      }
      // Store the full profile data for easy access
      profileStore.setProfileInfo(profileData);
    }
  }, [profileData]);

  return {
    isProfileLoading,
    profileData,
    profileResp,
    refetchProfile,
  };
};

/**
 * Custom hook for fetching portal access/applications.
 * Centralizes the portal access fetching logic.
 *
 * @param options - Additional React Query options
 * @returns Portals data, loading state, and refetch function
 */
export const usePortalAccess = (options: any = {}) => {
  const {
    data: portalsResp,
    isLoading: isPortalsLoading,
    refetch: refetchPortals,
  } = useFetchList<any>(['portalAccess'], 'auth/applications', {
    ssoLogin: true,
    ...options,
  });

  const portals =
    portalsResp?.data && Array.isArray(portalsResp.data)
      ? portalsResp.data
      : [];

  return {
    isPortalsLoading,
    portals,
    portalsResp,
    refetchPortals,
  };
};
